import { Link } from "react-router-dom";
import { Icon } from "@common/index";
import { ROUTES } from "@routing/routes";
import { LocationStatus, type LocationResponse } from "@/models/location";
import { LocationResubmitForm } from "@components/Locations/LocationResubmitForm";
import { rejectionLine } from "@components/Locations/_helpers/rejectionLine";

interface LocationStatusNoticeProps {
    location: LocationResponse;
    // Opens the certificate upload for this location. Without it, the
    // certificate-needed line has no button of its own: the location detail
    // page (#320) offers the upload in its certificate section instead.
    onUploadCertificate?: () => void;
    onResubmitted: () => void;
}

// What a location's approval state asks of the customer, as the Account ›
// Locations row says it. Shared with the location detail page (#320), so the
// two never word a status differently. Approved and Suspended say nothing
// here.
export const LocationStatusNotice = ({
    location,
    onUploadCertificate,
    onResubmitted,
}: LocationStatusNoticeProps) => (
    <>
        {/* #434: passed review, but its first-month
            charge has not gone through, so it is NOT
            approved and can't be ordered for. A card
            save or a membership renewal that goes
            through activates it. The customer sees
            the same state whatever the charge outcome
            was, so the support path covers a refusal
            on our side or an unconfirmed charge. */}
        {location.status === LocationStatus.PendingPayment && (
            <p className="mt-2 flex items-start gap-1 text-sm text-neutral">
                <Icon name="schedule" />
                <span>
                    Passed review — waiting for
                    payment. This location can&apos;t
                    be used for orders yet.{" "}
                    <Link
                        to={ROUTES.ACCOUNT_MEMBERSHIP}
                        className="link link-primary"
                    >
                        Update your card
                    </Link>{" "}
                    to activate it, or{" "}
                    <Link
                        to={ROUTES.SUPPORT}
                        className="link link-primary"
                    >
                        contact support
                    </Link>{" "}
                    if your card is up to date.
                </span>
            </p>
        )}
        {/* #437: approval skips a location with no
            resale certificate, so a pending one
            without one asks for it. One that has
            it (the location from /apply, or one
            already uploaded for) is just in the
            queue; the dashboard's upload still
            offers it for a replacement. Only an
            ACTIVE location: the upload takes no
            other, so a removed one offers none. */}
        {location.isActive &&
            location.status === LocationStatus.Pending &&
            (location.hasCertificate === false ? (
                <p className="mt-2 flex items-start gap-1 text-sm text-neutral">
                    <Icon name="upload_file" />
                    <span>
                        Certificate needed — upload
                        it so we can review this
                        location.
                        {onUploadCertificate && (
                            <>
                                {" "}
                                <button
                                    type="button"
                                    className="link link-primary"
                                    onClick={onUploadCertificate}
                                >
                                    Upload certificate
                                </button>
                            </>
                        )}
                    </span>
                </p>
            ) : (
                <p className="mt-2 flex items-start gap-1 text-sm text-neutral">
                    <Icon name="schedule" />
                    <span>Waiting for review.</span>
                </p>
            ))}
        {/* OB-09 (ruling 2026-10-20): a rejected
            location is resubmitted where its status
            shows (its list row, or its detail page),
            so an approved customer with one
            rejection is not stuck. The status shows
            on any rejected location; the form hides
            itself for a removed one, whose resubmit
            the API 404s. */}
        {location.status === LocationStatus.Rejected && (
            <div className="mt-2">
                <p className="flex items-start gap-1 text-sm text-neutral">
                    <Icon name="block" />
                    <span>{rejectionLine(location)}</span>
                </p>
                <LocationResubmitForm
                    location={location}
                    onResubmitted={onResubmitted}
                />
            </div>
        )}
    </>
);
